import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faFileVideo,
	faPlus,
	faUpload,
} from "@fortawesome/free-solid-svg-icons"
import { URL } from "../../store/actions/depannageActions"
import CustomModal, {
	DeletingCustomModal,
} from "../basics/interface/CustomModal"
import AppPhotosViewer from "../basics/interface/AppPhotos"
import { COLORS } from "../../utilities/Constants"
import { PdfTile } from "../basics/interface/AppPdfs"

// Affichage V2 des fichiers d'une catégorie, calqué sur les photos avant/après de
// Location (AppPhotos) : vignettes carrées 90×90, suppression en croix ronde au
// survol (cohérent avec PdfSlot des transactions), et aperçu via le viewer
// scrollable AppPhotosViewer pour les images. PDF/vidéos : ouverture en onglet.
// API inchangée pour les appelants (FileSection).

// Dimension commune à toutes les tuiles (cartes / "+" / en attente).
export const CARD_W = 90
const TILE = 90

const IMG_EXT = ["jpg", "jpeg", "png", "gif", "webp", "bmp"]

const extOf = (f) => (f.url || f.name || "").split(".").pop().toLowerCase()
export const isImage = (f) => IMG_EXT.includes(extOf(f))
const isVideo = (f) => extOf(f) === "mp4"

// URL absolue propre (évite le double slash entre URL et file.url).
export const srcOf = (f) => {
	const base = URL.endsWith("/") ? URL.slice(0, -1) : URL
	const path = f.url.startsWith("/") ? f.url : `/${f.url}`
	return `${base}${path}`
}

const fileLabel = (f) =>
	f.name ? f.name : (f.url || "").split("/").pop() || "Fichier"

const captionBar = {
	position: "absolute",
	bottom: 0,
	left: 0,
	right: 0,
	padding: "3px 6px",
	backgroundColor: "rgba(28,25,23,0.6)",
	fontSize: 9,
	color: "#fff",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
}

// --- Vignette d'un fichier sauvegardé (style AppPhotoThumbnail) ---------------
const FileCard = ({ file, canDelete, onOpen, onDelete }) => {
	const [hover, setHover] = useState(false)
	const img = isImage(file)
	const vid = isVideo(file)

	if (!img && !vid) {
		return (
			<PdfTile
				doc={{ url: srcOf(file), name: fileLabel(file) }}
				onOpen={onOpen}
				onDelete={canDelete ? onDelete : undefined}
				readOnly={!canDelete}
				size={TILE}
				height={TILE}
			/>
		)
	}

	return (
		<div
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			onClick={onOpen}
			title={fileLabel(file)}
			style={{
				position: "relative",
				width: TILE,
				height: TILE,
				borderRadius: 10,
				overflow: "hidden",
				border: `1px solid ${COLORS.border}`,
				backgroundColor: COLORS.bg,
				flexShrink: 0,
				cursor: "pointer",
			}}
		>
			{img ? (
				<img
					src={srcOf(file)}
					alt=""
					style={{
						width: "100%",
						height: "100%",
						objectFit: "cover",
						display: "block",
						transition: "transform 0.2s",
						transform: hover ? "scale(1.05)" : "scale(1)",
					}}
				/>
			) : (
				<div
					style={{
						width: "100%",
						height: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<FontAwesomeIcon
						icon={faFileVideo}
						style={{
							fontSize: 28,
							color: COLORS.textMuted,
						}}
					/>
				</div>
			)}

			{canDelete && hover && (
				<button
					type="button"
					title="Supprimer"
					onClick={(e) => {
						e.stopPropagation()
						onDelete()
					}}
					style={{
						position: "absolute",
						top: 0,
						right: 0,
						width: 28,
						height: 28,
						borderRadius: "50%",
						border: "none",
						backgroundColor: COLORS.danger,
						color: "#fff",
						fontSize: 16,
						fontWeight: 300,
						lineHeight: 1,
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					×
				</button>
			)}

			<div style={captionBar}>{fileLabel(file)}</div>
		</div>
	)
}

// --- Tuile "Ajouter" (fin de galerie) ----------------------------------------
export const AddFileTile = ({ onClick }) => {
	const [hover, setHover] = useState(false)
	return (
		<div
			onClick={onClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				width: TILE,
				height: TILE,
				borderRadius: 10,
				border: `1px dashed ${COLORS.borderStrong}`,
				backgroundColor: hover ? COLORS.surface : COLORS.bg,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				cursor: "pointer",
				color: COLORS.textLight,
				flexShrink: 0,
				transition: "background-color 0.15s",
			}}
		>
			<FontAwesomeIcon icon={faPlus} style={{ fontSize: 20 }} />
		</div>
	)
}

// --- Vignette d'un fichier en attente d'upload -------------------------------
export const PendingFileCard = ({ name }) => (
	<div
		title={name}
		style={{
			position: "relative",
			width: TILE,
			height: TILE,
			borderRadius: 10,
			overflow: "hidden",
			border: `1px dashed ${COLORS.borderStrong}`,
			backgroundColor: COLORS.bg,
			flexShrink: 0,
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: 6,
			color: COLORS.textLight,
		}}
	>
		<FontAwesomeIcon icon={faUpload} style={{ fontSize: 18 }} />
		<span
			style={{
				fontSize: 8,
				fontWeight: 600,
				textTransform: "uppercase",
				letterSpacing: "0.06em",
				color: COLORS.warning,
			}}
		>
			À uploader
		</span>
		<div style={captionBar}>{name}</div>
	</div>
)

const FileRenderer = ({
	files,
	deleteFile,
	postDelete,
	resetDelete,
	canDelete,
}) => {
	const [showDelete, setShowDelete] = useState({ show: false, id: null })
	const [viewer, setViewer] = useState(null) // { images, index } | null

	const closeShowDelete = () => {
		setShowDelete({ show: false, id: null })
		if (resetDelete) resetDelete()
	}

	const saved = (files || []).filter((f) => f.saved || f.id)
	const images = saved.filter(isImage)

	const openFile = (file) => {
		if (isImage(file)) {
			const idx = images.findIndex(
				(f) => (f.url || f.id) === (file.url || file.id),
			)
			setViewer({
				images: images.map((f) => ({
					url: srcOf(f),
					name: fileLabel(f),
				})),
				index: Math.max(0, idx),
			})
		} else {
			// PDF / autre : ouverture dans un nouvel onglet.
			window.open(srcOf(file))
		}
	}

	return (
		<>
			{showDelete.show &&
				(postDelete &&
				(postDelete.deleting ||
					postDelete.deleted ||
					postDelete.error) ? (
					<DeletingCustomModal
						status={postDelete}
						onClose={closeShowDelete}
					/>
				) : (
					<CustomModal
						title="Supprimer le fichier ?"
						onClose={closeShowDelete}
						showCloseButton={false}
						buttons={[
							{
								title: "Annuler",
								styleName: "secondary",
								onClick: closeShowDelete,
							},
							{
								title: "Supprimer",
								styleName: "danger",
								onClick: () => {
									deleteFile(showDelete.id)
									// Sans slice de statut (`postDelete`), aucun
									// DeletingCustomModal ne prendra le relais pour
									// fermer : on ferme nous-mêmes après l'appel
									// (suppression optimiste, ex. fiche client).
									if (!postDelete) closeShowDelete()
								},
							},
						]}
					>
						<div style={{ color: COLORS.textMuted, fontSize: 14 }}>
							Cette action est définitive.
						</div>
					</CustomModal>
				))}
			{viewer && (
				<AppPhotosViewer
					images={viewer.images}
					currentIndex={viewer.index}
					onClose={() => setViewer(null)}
					onNavigate={(index) =>
						setViewer((v) => (v ? { ...v, index } : v))
					}
				/>
			)}
			{saved.map((file) => (
				<FileCard
					key={file.url || file.id}
					file={file}
					canDelete={canDelete}
					onOpen={() => openFile(file)}
					onDelete={() => setShowDelete({ show: true, id: file.id })}
				/>
			))}
		</>
	)
}

export default FileRenderer
